import React, { useMemo } from "react";
import { Alert, Button, Modal, Progress, Typography } from "antd";
import type { OrderReprocessProgress } from "../../types/orders";
import { formatReprocessErrorDetail } from "../../types/orders";
import "./OrderReprocessProgress.css";

interface OrderReprocessProgressModalProps {
  open: boolean;
  progress: OrderReprocessProgress | null;
  onClose: () => void;
}

const OrderReprocessProgressModal: React.FC<
  OrderReprocessProgressModalProps
> = ({ open, progress, onClose }) => {
  const isDone =
    progress?.status === "COMPLETED" || progress?.status === "FAILED";
  const isRunning = progress?.status === "RUNNING" || (!progress && open);
  const percent = progress?.percent ?? (isRunning ? 8 : 0);
  const status =
    progress?.status === "FAILED"
      ? "exception"
      : progress?.status === "COMPLETED"
        ? "success"
        : "active";

  const failedResults = useMemo(
    () => progress?.results?.filter((r) => !r.success || r.skipped) ?? [],
    [progress?.results]
  );

  const failedDetails = useMemo(
    () =>
      failedResults.map((r) => ({
        orderId: r.orderId,
        skipped: r.skipped,
        detail: formatReprocessErrorDetail(r.errorDetail, r.message),
      })),
    [failedResults]
  );

  const alertSummary = useMemo(() => {
    if (!isDone || failedDetails.length === 0) return null;
    const first = failedDetails[0]?.detail.summary ?? "";
    const allSame =
      first.length > 0 &&
      failedDetails.every((item) => item.detail.summary === first);
    return allSame ? first : null;
  }, [failedDetails, isDone]);

  const title = isDone
    ? progress?.status === "FAILED"
      ? "Reproceso finalizado con errores"
      : "Reproceso finalizado"
    : "Reprocesando a SIESA…";

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      footer={
        isDone ? (
          <div className="order-reprocess-progress__footer">
            <Button type="primary" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        ) : null
      }
      maskClosable={isDone}
      closable={isDone}
      keyboard={isDone}
      destroyOnClose
      centered
      width={560}
      className="order-reprocess-progress"
    >
      {!progress ? (
        <Typography.Text type="secondary">Preparando lote…</Typography.Text>
      ) : (
        <>
          <Progress
            percent={isDone ? percent : Math.max(percent, 8)}
            status={status}
            showInfo={isDone}
          />

          <div className="order-reprocess-progress__stats">
            <div>
              <Typography.Text type="secondary">Total</Typography.Text>
              <div className="order-reprocess-progress__value">
                {progress.total}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Procesadas</Typography.Text>
              <div className="order-reprocess-progress__value">
                {progress.processed}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Pendientes</Typography.Text>
              <div className="order-reprocess-progress__value">
                {progress.pending}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Exitosas</Typography.Text>
              <div className="order-reprocess-progress__value order-reprocess-progress__value--ok">
                {progress.successCount}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">Con novedad</Typography.Text>
              <div className="order-reprocess-progress__value order-reprocess-progress__value--warn">
                {progress.noveltyCount}
              </div>
            </div>
          </div>

          {isRunning && progress.currentOrderVTEXId && (
            <Typography.Paragraph className="order-reprocess-progress__current">
              Orden en curso:{" "}
              <Typography.Text strong copyable>
                {progress.currentOrderVTEXId}
              </Typography.Text>
            </Typography.Paragraph>
          )}

          <Alert
            type={
              progress.status === "FAILED"
                ? "error"
                : progress.status === "COMPLETED"
                  ? progress.noveltyCount > 0
                    ? "warning"
                    : "success"
                  : "info"
            }
            showIcon
            message={
              isRunning
                ? `Reprocesando lote de ${progress.total} orden(es)…`
                : alertSummary || progress.message
            }
            description={
              isDone
                ? `Resultado: ${progress.successCount} exitosas, ${progress.noveltyCount} con novedad u omitidas.`
                : "No cierres esta ventana hasta que termine el lote."
            }
          />

          {isDone && failedDetails.length > 0 && (
            <div className="order-reprocess-progress__results">
              <Typography.Text type="secondary">
                Detalle por orden ({failedDetails.length}):
              </Typography.Text>
              <ul className="order-reprocess-progress__result-list">
                {failedDetails.slice(0, 12).map((item) => (
                  <li key={item.orderId} className="order-reprocess-progress__result-item">
                    <div className="order-reprocess-progress__result-order">
                      <Typography.Text strong copyable>
                        {item.orderId}
                      </Typography.Text>
                      {item.skipped ? (
                        <Typography.Text type="secondary">
                          {" "}
                          (omitida)
                        </Typography.Text>
                      ) : null}
                    </div>
                    <div className="order-reprocess-progress__result-summary">
                      {item.detail.summary}
                    </div>
                    {item.detail.lines.length > 0 && (
                      <ul className="order-reprocess-progress__result-lines">
                        {item.detail.lines.slice(0, 5).map((line, idx) => (
                          <li key={`${item.orderId}-line-${idx}`}>{line}</li>
                        ))}
                        {item.detail.lines.length > 5 && (
                          <li>…y {item.detail.lines.length - 5} más</li>
                        )}
                      </ul>
                    )}
                  </li>
                ))}
                {failedDetails.length > 12 && (
                  <li>…y {failedDetails.length - 12} órdenes más</li>
                )}
              </ul>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default OrderReprocessProgressModal;
